// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getProjectFulfillmentBySku } from '@/project/api/get-project-fulfillment-by-sku';
import { ProjectFulfillmentBySku } from '@/project/api/types';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';

/**
 * Query key for the project fulfillment-by-SKU cache. Exported so mutations
 * that change FO state on a project (create/update/cancel) can invalidate
 * this cache and force the dialog to recompute Need on the next open.
 */
export const projectFulfillmentBySkuQueryKey = (projectKey: string) =>
  ['project', projectKey, 'fulfillment-by-sku'] as const;

/**
 * Fetches the per-SKU fulfillment summary for a project. The underlying query
 * does SUM / GROUP BY on fulfillment_order_items in SQL and filters cancelled
 * FOs, so pagination size is not a correctness concern here.
 */
export function useProjectFulfillmentBySku(
  projectKey: string
): ProjectFulfillmentBySku {
  const { data } = useSuspenseQuery({
    queryKey: projectFulfillmentBySkuQueryKey(projectKey),
    queryFn: () =>
      getProjectFulfillmentBySku(projectKey).then((response) => response.data),
  });
  return data;
}

/**
 * Non-suspending variant for callers that need the summary alongside other
 * non-suspense queries (e.g. forms that already coordinate `useProject` and
 * inventory lookups). When `projectKey` is null the query is disabled.
 */
export function useProjectFulfillmentBySkuOptional(projectKey: string | null): {
  data: ProjectFulfillmentBySku | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const result = useQuery({
    queryKey: projectFulfillmentBySkuQueryKey(projectKey ?? ''),
    queryFn: () => {
      if (!projectKey) {
        throw new Error('projectKey is required when query is enabled');
      }
      return getProjectFulfillmentBySku(projectKey).then(
        (response) => response.data
      );
    },
    enabled: !!projectKey,
  });
  return {
    data: result.data,
    isLoading: result.isLoading,
    isError: result.isError,
    refetch: () => {
      void result.refetch();
    },
  };
}
